import { Routes } from '@angular/router';

import { customerAuthGuard } from './core/auth/auth.guard';
import { establishmentOnlyGuard } from './core/auth/establishment-only.guard';
import { PanelLayoutComponent } from './core/layout/panel-layout.component';
import { PublicLayoutComponent } from './core/layout/public-layout.component';
import { APP_PATHS } from './core/routing/app-paths';
import { AUTH_ROUTES } from './features/auth/auth.routes';

// Composition only: layouts + lazy features. Every slice follows this pattern —
// a feature adds its own `<obszar>.routes.ts` and gets wired here via `loadChildren`.
export const routes: Routes = [
  {
    path: APP_PATHS.panel,
    component: PanelLayoutComponent,
    canActivate: [customerAuthGuard],
    // Q-188/A — TA SAMA funkcja guarda, drugi hak. `canActivate` biegnie tylko przy
    // WEJŚCIU w gałąź; nawigacja MIĘDZY sekcjami panelu (kliknięcie w sidenav) nie
    // aktywuje tego rodzica ponownie, więc bez tej linii sesja martwa w środku pracy
    // zostawiała klienta w ŻYWYM panelu z pełnym sidenavem. Po Q-178 sesja może umrzeć
    // na sześciu ścieżkach (zmiana hasła, reset, usunięcie konta, ponowna rejestracja,
    // aktywacja, potwierdzenie zmiany adresu), a nie tylko przez wygaśnięcie — „martwy
    // panel" był więc klasą, nie incydentem, i domykały go dotąd łatki punktowe
    // w trzech miejscach naraz.
    //
    // JEDNA reguła w JEDNYM miejscu, celowo NIE reakcja w `PanelLayoutComponent`:
    // tamto byłoby drugą implementacją tej samej reguły, a takie się rozjeżdżają
    // (w tym repo są trzy wystąpienia klasy „cache tożsamości" dokładnie z tego powodu).
    //
    // NIE kosztuje drugiego żądania: guard woła `refresh()` wyłącznie przy statusie
    // `unknown`, a ten rozstrzyga się w PIERWSZYM z dwóch przebiegów jednej nawigacji
    // (dowód: app.routes.spec — „nie odpytuje serwera dwa razy w jednej nawigacji").
    canActivateChild: [customerAuthGuard],
    title: 'Panel klienta — EMPIRIA',
    // Sekcje panelu (S-02+) wchodzą tu jako leniwe children. `canActivate` NIE
    // jest powtarzane w children — guard stoi na tym rodzicu, a `canActivateChild`
    // wyżej obejmuje KAŻDE z nich bez ani jednego powtórzenia wpisu.
    children: [
      // Domyślna sekcja panelu — od P2 („Poprawki do panelu klienta") KALENDARZ,
      // nie „Moje szkolenia": kalendarz jest dashboardem panelu. Przekierowanie,
      // nie `loadChildren` na pustej ścieżce: router nie cofa się do kolejnego
      // rodzeństwa, gdy leniwa konfiguracja z pustą ścieżką nie skonsumuje URL-a
      // (dowód niżej, przy gałęzi publicznej).
      { path: '', pathMatch: 'full', redirectTo: APP_PATHS.calendar },
      {
        // Kalendarz szkoleń (S-07) — do P2 trasa publiczna `/kalendarz`, teraz
        // dashboard panelu. Bez własnego guarda: sesję egzekwuje rodzic. Komórki
        // siatki linkują do kalendarium (`panel/kalendarium/:id`).
        path: APP_PATHS.calendar,
        loadChildren: () =>
          import('./features/calendar/calendar.routes').then((m) => m.CALENDAR_ROUTES),
      },
      {
        // Kalendarium (S-06, dawny „Katalog szkoleń") — do P2 trasa publiczna
        // `/katalog`, teraz sekcja panelu pod nową nazwą. Bez własnego guarda:
        // sesję egzekwuje rodzic; endpointy backendu zostają `@Public()`.
        path: APP_PATHS.calendarium,
        loadChildren: () =>
          import('./features/catalog/catalog.routes').then((m) => m.CATALOG_ROUTES),
      },
      {
        path: APP_PATHS.myCourses,
        loadChildren: () =>
          import('./features/my-courses/my-courses.routes').then((m) => m.MY_COURSES_ROUTES),
      },
      {
        // Segment NIEPUSTY, tak jak wyżej: leniwa konfiguracja na pustej ścieżce,
        // która nie skonsumuje URL-a, nie oddaje sterowania kolejnemu rodzeństwu.
        path: APP_PATHS.myRegistrations,
        loadChildren: () =>
          import('./features/my-registrations/my-registrations.routes').then(
            (m) => m.MY_REGISTRATIONS_ROUTES,
          ),
      },
      {
        // Segment NIEPUSTY z tego samego powodu co wyżej.
        path: APP_PATHS.certificates,
        loadChildren: () =>
          import('./features/certificates/certificates.routes').then((m) => m.CERTIFICATES_ROUTES),
      },
      {
        // Segment NIEPUSTY z tego samego powodu co wyżej. Dostęp do abonamentów
        // egzekwuje BACKEND (403 dla klienta niebędącego placówką) — trasa jest
        // otwarta dla każdej sesji panelu, a ukrycie pozycji w sidenavie to
        // uprzejmość w prezentacji, nie granica bezpieczeństwa.
        path: APP_PATHS.mySubscriptions,
        loadChildren: () =>
          import('./features/my-subscriptions/my-subscriptions.routes').then(
            (m) => m.MY_SUBSCRIPTIONS_ROUTES,
          ),
      },
      {
        // Segment NIEPUSTY z tego samego powodu co wyżej. Jedyna trasa panelu z
        // WŁASNYM guardem obok sesyjnego na rodzicu: `establishmentOnlyGuard` to
        // INNY guard (typ konta, nie sesja) i dotyczy WYŁĄCZNIE tej sekcji (Q-63/A)
        // — formularz zapotrzebowania jest funkcją placówki, a klient indywidualny
        // odbija na domyślną sekcję panelu. Granicą dostępu pozostaje backend
        // (403 na POST i GET …/offer); guard tylko chowa wejście.
        path: APP_PATHS.closedCourseRequest,
        canActivate: [establishmentOnlyGuard],
        loadChildren: () =>
          import('./features/closed-course-request/closed-course-request.routes').then(
            (m) => m.CLOSED_COURSE_REQUEST_ROUTES,
          ),
      },
      {
        // Segment NIEPUSTY z tego samego powodu co wyżej. BEZ własnego guardu, w
        // przeciwieństwie do sąsiada powyżej: „Mój profil" (S-12) istnieje dla OBU
        // typów konta i różni je tylko zestaw renderowanych sekcji. Granicą jest
        // backend — sekcja cudzego typu konta odpowiada 403 — a wybór sekcji na
        // ekranie jest uprzejmością prezentacji (Q-63/A, ten sam precedens).
        path: APP_PATHS.myProfile,
        loadChildren: () =>
          import('./features/my-profile/my-profile.routes').then((m) => m.MY_PROFILE_ROUTES),
      },
      {
        // Segment NIEPUSTY z tego samego powodu co wyżej. BEZ własnego guardu, jak
        // „Mój profil": centrum powiadomień (S-16) istnieje dla OBU typów konta —
        // zdarzenia, o których mówi, zdarzają się i osobie prywatnej, i placówce.
        // Granicą jest sesja z rodzica plus backend: `/my/notifications` czyta
        // ciasteczko sesji i zwraca wyłącznie wiersze tego konta.
        path: APP_PATHS.notifications,
        loadChildren: () =>
          import('./features/notifications/notifications.routes').then(
            (m) => m.NOTIFICATIONS_ROUTES,
          ),
      },
      // PRZEKIEROWANIA, nie ekrany (P9/P10, „Poprawki do panelu klienta"): zmiana
      // hasła (S-13) i zmiana adresu logowania (S-14) stoją teraz jako karty na dole
      // „Mojego profilu”, a nie pod własnymi trasami. Segmenty ZOSTAJĄ jako redirecty,
      // bo do obu adresów mogły istnieć zakładki i linki — `redirectTo` WZGLĘDNE
      // rozwiązuje się względem rodzica `panel`, czyli na `/panel/profil` (ten sam
      // mechanizm co domyślne przekierowanie panelu wyżej).
      { path: APP_PATHS.changePassword, redirectTo: APP_PATHS.myProfile },
      { path: APP_PATHS.changeEmail, redirectTo: APP_PATHS.myProfile },
      {
        // Kontakt (S-17) — do P2 trasa publiczna `/kontakt`, teraz sekcja panelu.
        // Ekran dalej nie robi żadnego HTTP; za guard rodzica wjechał razem z
        // resztą treści, bo część publiczna przestała istnieć.
        path: APP_PATHS.contact,
        loadChildren: () =>
          import('./features/contact/contact.routes').then((m) => m.CONTACT_ROUTES),
      },
    ],
  },
  // Trasy auth stoją POZA oboma layoutami (P4, „Poprawki do panelu klienta"): każdy
  // z tych ekranów renderuje pełnoekranowy `AuthShellComponent`, więc header i stopka
  // layoutu publicznego nie mają tu czego dodać — dokładnie tak, jak panel admina
  // chowa menu na `/auth`. Wpisy MUSZĄ stać NAD gałęzią `path: ''`: jej `path: '**'`
  // skonsumowałby każdy z tych segmentów, a router nie cofa się do pominiętego
  // rodzeństwa. Rozwinięcie zamiast `loadChildren` z tego samego powodu co dawniej
  // pod layoutem publicznym: leniwość zostaje na komponentach (`loadComponent`),
  // więc do głównego bundle'a trafia tylko sama tablica tras.
  ...AUTH_ROUTES,
  // GAŁĄŹ PUBLICZNA JEST SZCZĄTKOWA (P2, „Poprawki do panelu klienta"): aplikacja
  // jest wyłącznie panelem klienta, więc strona główna, katalog, kalendarz i kontakt
  // zniknęły stąd — dwa ostatnie żyją dalej jako sekcje panelu, katalog jako
  // „Kalendarium". Pod layoutem publicznym zostają tylko `/zapis` i 404.
  {
    path: APP_PATHS.home,
    component: PublicLayoutComponent,
    children: [
      {
        // Zapis na szkolenie otwarte (S-09) — JEDYNA merytoryczna trasa publiczna,
        // decyzją Filipa (2026-08-17): linki z mailingów i strony biura muszą
        // działać bez logowania. Trasa ŚWIADOMA SESJI: ten sam ekran obsługuje
        // anonima i zalogowanego, więc `canActivate` byłoby tu szkodliwe (guard
        // odbiłby deep-link z maila na logowanie i lej straciłby zgłoszenie).
        // Wpis stoi NAD `path: ''` i `path: '**'`, bo router nie cofa się do
        // pominiętego rodzeństwa (dowód: app.routes.spec).
        path: APP_PATHS.courseSignup,
        loadChildren: () =>
          import('./features/course-signup/course-signup.routes').then(
            (m) => m.COURSE_SIGNUP_ROUTES,
          ),
      },
      {
        // `/` prowadzi do logowania, nie do strony głównej — strona główna nie
        // istnieje. Anonim ląduje na formularzu; zalogowanego, który wejdzie w
        // `/panel`, guard i tak wpuści, więc osobne rozgałęzienie po sesji jest
        // tu zbędne. Redirect ABSOLUTNY z konieczności: względny rozwiązuje się
        // wśród RODZEŃSTWA tej trasy (dzieci layoutu publicznego), gdzie segment
        // `logowanie` nie istnieje i konsumował go wildcard — `/` kończyło na
        // 404 zamiast na pełnoekranowym AuthShellu z P4 (bug znaleziony w
        // przebiegu weryfikacyjnym; dowód: app.routes.spec).
        path: '',
        pathMatch: 'full',
        redirectTo: `/${APP_PATHS.login}`,
      },
      {
        path: '**',
        title: 'Nie znaleziono strony — EMPIRIA',
        loadComponent: () =>
          import('./shared/not-found/not-found.component').then((m) => m.NotFoundComponent),
      },
    ],
  },
];
